import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Star, 
  TrendingUp, 
  AlertTriangle,
  DollarSign,
  Users,
  Activity,
  ChevronRight,
  Sparkles,
  Clock,
  MessageSquare
} from 'lucide-react';
import { KPICard } from '../../components/shared/KPICard';
import { PatientListItem } from '../../components/shared/PatientListItem';
import { AIInsightPanel } from '../../components/shared/AIInsightCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { RiskBadge } from '../../components/shared/RiskBadge';

const kpiData = [
  { title: "Today's Sessions", value: '5', icon: Calendar, description: '2 completed, 3 upcoming' },
  { title: 'Caseload Health', value: '87%', change: '+4%', changeType: 'positive', icon: Activity },
  { title: 'At-Risk Patients', value: '3', icon: AlertTriangle, description: 'Need attention' },
  { title: 'Avg Rating', value: '4.9', icon: Star, description: 'From 156 reviews' },
  { title: 'Compliance Rate', value: '82%', change: '+6%', changeType: 'positive', icon: TrendingUp },
  { title: 'Earnings', value: 'RM 8,450', icon: DollarSign, description: 'This month' },
];

const todaySessions = [
  { id: 1, patient: 'Ahmad Ismail', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', time: '9:00 AM', duration: '45 min', type: 'In-person', status: 'completed', condition: 'Lower Back Pain' },
  { id: 2, patient: 'Sarah Lee', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100', time: '10:30 AM', duration: '60 min', type: 'In-person', status: 'completed', condition: 'ACL Recovery' },
  { id: 3, patient: 'Ravi Kumar', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100', time: '2:00 PM', duration: '45 min', type: 'Telehealth', status: 'upcoming', condition: 'Shoulder Rehab' },
  { id: 4, patient: 'Mei Ling Wong', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100', time: '3:30 PM', duration: '45 min', type: 'In-person', status: 'upcoming', condition: 'Post-Surgery' },
  { id: 5, patient: 'David Tan', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100', time: '5:00 PM', duration: '30 min', type: 'Follow-up', status: 'upcoming', condition: 'Neck Pain' },
];

const focusPatients = [
  { name: 'Mei Ling Wong', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100', condition: 'Post-Surgery', riskLevel: 'high', compliance: 58, nextSession: 'Today 3:30 PM', serviceType: 'Physio' },
  { name: 'Ahmad Ismail', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', condition: 'Lower Back Pain', riskLevel: 'medium', compliance: 72, nextSession: 'Tomorrow 9:00 AM', serviceType: 'Physio' },
  { name: 'John Ng', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100', condition: 'Stroke Rehab', riskLevel: 'medium', compliance: 65, nextSession: 'Wed 10:00 AM', serviceType: 'OT' },
];

const improvingPatients = [
  { name: 'Sarah Lee', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100', condition: 'ACL Recovery', riskLevel: 'low', compliance: 94, improvement: '+12% this week', serviceType: 'Physio' },
  { name: 'Priya Devi', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100', condition: 'Tennis Elbow', riskLevel: 'low', compliance: 88, improvement: '+8% this week', serviceType: 'Physio' },
];

const aiInsights = [
  {
    title: 'Mei Ling needs plan adjustment',
    reason: 'Progress stalled for 2 weeks. Consider reducing intensity and adding mobility work.',
    confidence: 85,
    type: 'risk',
    actions: ['Adjust plan', 'Schedule call']
  },
  {
    title: 'Ahmad ready for progression',
    reason: 'Pain scores consistently low, strength improving. Ready for Phase 2 exercises.',
    confidence: 92,
    type: 'improvement',
    actions: ['Accept suggestion', 'Review exercises']
  },
  {
    title: 'Send encouragement to John',
    reason: 'Missed 2 sessions, engagement dropping. Supportive message may help.',
    confidence: 78,
    type: 'compliance',
    actions: ['Send message', 'View history']
  },
];

export default function TherapistDashboard() {
  const navigate = useNavigate();
  const [patientTab, setPatientTab] = useState('focus');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Good morning, Dr. Chen</h1>
          <p className="text-sm text-muted-foreground mt-1">Here's your AI-powered caseload overview</p>
        </div>
        <Button onClick={() => navigate('/therapist/chat')}>
          <Sparkles className="w-4 h-4 mr-2" />
          AI Assistant
        </Button>
      </div>
      
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiData.map((kpi, idx) => (
          <KPICard key={idx} {...kpi} index={idx} />
        ))}
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - Sessions & Patients */}
        <div className="xl:col-span-2 space-y-6">
          {/* Today's Sessions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-base font-display">Today's Schedule</CardTitle>
                <CardDescription>5 sessions scheduled</CardDescription>
              </div>
              <Button variant="ghost" size="sm">
                View all <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {todaySessions.map((session, idx) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-4 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/therapist/patient/${session.id}`)}
                  >
                    <div className="text-center min-w-[70px]">
                      <p className="text-sm font-medium text-foreground">{session.time}</p>
                      <p className="text-xs text-muted-foreground">{session.duration}</p>
                    </div>
                    
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={session.avatar} />
                      <AvatarFallback>{session.patient.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">{session.patient}</p>
                      <p className="text-sm text-muted-foreground">{session.condition}</p>
                    </div>
                    
                    <Badge variant={session.status === 'completed' ? 'secondary' : 'default'}>
                      {session.status === 'completed' ? 'Done' : session.type}
                    </Badge>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Patient Lists */}
          <Card>
            <CardHeader>
              <Tabs value={patientTab} onValueChange={setPatientTab}>
                <TabsList>
                  <TabsTrigger value="focus" className="gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Focus Today
                  </TabsTrigger>
                  <TabsTrigger value="improving" className="gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Improving Well
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent>
              {patientTab === 'focus' ? (
                <div className="space-y-3">
                  {focusPatients.map((patient, idx) => (
                    <PatientListItem 
                      key={idx} 
                      patient={patient} 
                      index={idx}
                      onClick={() => navigate(`/therapist/patient/${idx + 1}`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {improvingPatients.map((patient, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center gap-4 p-4 rounded-xl border border-border bg-success-light/30 cursor-pointer"
                      onClick={() => navigate(`/therapist/patient/${idx + 10}`)}
                    >
                      <Avatar className="w-11 h-11">
                        <AvatarImage src={patient.avatar} />
                        <AvatarFallback>{patient.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-foreground">{patient.name}</h4>
                          <RiskBadge level={patient.riskLevel} size="sm" />
                        </div>
                        <p className="text-sm text-muted-foreground">{patient.condition}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-success">{patient.improvement}</p>
                        <p className="text-xs text-muted-foreground">Compliance: {patient.compliance}%</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Right Column - AI Insights */}
        <AIInsightPanel 
          title="AI Recommendations" 
          insights={aiInsights}
        />
      </div>
    </div>
  );
}
